import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, TrendingDown, Clock, CloudRain,
  ExternalLink,
} from 'lucide-react';
import { eggWatchAdminAPI, EggWatchAdminAlert } from '../../../services/eggWatchAdminAPI';
import './EggWatchAdmin.css';

const alertTypeConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  production_drop: { label: 'Production Drop', icon: <TrendingDown size={14} />, color: 'red' },
  not_logging: { label: 'Not Logging', icon: <Clock size={14} />, color: 'yellow' },
  weather: { label: 'Weather', icon: <CloudRain size={14} />, color: 'blue' },
  feed_opportunity: { label: 'Feed Opportunity', icon: <AlertTriangle size={14} />, color: 'green' },
};

const EggWatchAdminAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<EggWatchAdminAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const t = searchParams.get('type');
    if (t) setTypeFilter(t);
  }, [searchParams]);

  useEffect(() => {
    loadAlerts();
  }, [typeFilter, statusFilter]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (typeFilter) params.alert_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await eggWatchAdminAPI.getAdminAlerts(params);
      setAlerts(res.data);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const typeCounts = alerts.reduce<Record<string, number>>((acc, a) => {
    acc[a.alert_type] = (acc[a.alert_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="egg-watch-admin">
      <button className="ewa-back-button" onClick={() => navigate('/planner/egg-watch')}>
        <ArrowLeft size={16} /> Back to Egg Watch
      </button>

      <div className="ewa-header">
        <div className="ewa-header-title">
          <AlertTriangle className="ewa-header-icon" />
          <div>
            <h1>Egg Watch Alerts</h1>
            <p className="ewa-header-subtitle">{alerts.length} alert{alerts.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Type Filter Chips */}
      <div className="ewa-filter-chips">
        <div
          className={`ewa-filter-chip ${!typeFilter ? 'active' : ''}`}
          onClick={() => setTypeFilter('')}
        >
          All <span className="chip-count">{alerts.length}</span>
        </div>
        {Object.entries(alertTypeConfig).map(([key, cfg]) => (
          <div
            key={key}
            className={`ewa-filter-chip ${typeFilter === key ? 'active' : ''}`}
            onClick={() => setTypeFilter(typeFilter === key ? '' : key)}
          >
            {cfg.icon} {cfg.label} <span className="chip-count">{typeCounts[key] || 0}</span>
          </div>
        ))}
      </div>

      {/* Status Toggle */}
      <div className="ewa-filters-bar">
        <select
          className="ewa-filter-select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="active">Active</option>
          <option value="reviewed">Reviewed</option>
          <option value="">All</option>
        </select>
      </div>

      {/* Alerts List */}
      {loading ? (
        <div className="ewa-loading-state">Loading alerts...</div>
      ) : alerts.length === 0 ? (
        <div className="ewa-empty-state">
          <AlertTriangle size={32} />
          <h3>No alerts</h3>
          <p>No alerts match the current filters.</p>
        </div>
      ) : (
        <div className="ewa-alerts-list">
          {alerts.map(alert => {
            const cfg = alertTypeConfig[alert.alert_type] || {
              label: alert.alert_type.replace(/_/g, ' '),
              icon: <AlertTriangle size={14} />,
              color: '',
            };

            return (
              <div key={alert.id} className={`ewa-admin-alert-card ${cfg.color}`}>
                <div className="ewa-alert-card-header">
                  <div className="ewa-alert-type-badge">
                    {cfg.icon} {cfg.label}
                  </div>
                  <span className={`ewa-severity-badge ${alert.severity}`}>{alert.severity}</span>
                </div>
                <div className="ewa-alert-card-content">
                  <span
                    className="ewa-alert-farm-name"
                    onClick={() => navigate(`/planner/egg-watch/farms/${alert.farm_id}`)}
                  >
                    {alert.farm_name} <ExternalLink size={12} />
                  </span>
                  <div className="ewa-alert-title">{alert.title}</div>
                  {alert.details && (
                    <div className="ewa-alert-details">
                      {Object.entries(alert.details).map(([key, val]) => (
                        <div key={key}>
                          <strong>{key.replace(/_/g, ' ')}:</strong> {String(val)}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="ewa-alert-timestamp">
                    Created {new Date(alert.created_at).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                    })}
                    {alert.reviewed_at && (
                      <> · Reviewed {new Date(alert.reviewed_at).toLocaleString('en-US', {
                        month: 'short', day: 'numeric',
                      })}</>
                    )}
                  </div>
                </div>
                <div className="ewa-alert-card-actions">
                  <button
                    className="ewa-btn-small primary"
                    onClick={() => navigate(`/planner/egg-watch/farms/${alert.farm_id}`)}
                  >
                    View Farm
                  </button>
                  {alert.action_taken && (
                    <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                      Action: {alert.action_taken}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EggWatchAdminAlerts;
